// src/components/admin/SubscriptionInquiryModal.jsx - Updated with Note System
import React, { useState, useEffect } from 'react';
import {
  FiX,
  FiUser,
  FiMail,
  FiBriefcase,
  FiCalendar,
  FiMessageCircle,
  FiSend,
  FiFlag,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiArchive,
  FiTrash2,
  FiClock,
  FiDollarSign,
  FiStar,
  FiTrendingUp,
  FiMessageSquare,
  FiLoader,
  FiCheck,
  FiAtSign,
  FiPlus,
  FiEdit2,
  FiTrash,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';

// API endpoint for sending response emails
const NOTIFY_API_URL = '/api/notify';

async function sendResponseEmail(
  clientEmail,
  clientName,
  responseMessage,
  packageName,
  inquiryId,
) {
  try {
    const apiResponse = await fetch(NOTIFY_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'admin-response',
        inquiryId: inquiryId,
        response: responseMessage,
        clientEmail: clientEmail,
        clientName: clientName,
        packageName: packageName || 'Subscription Plan',
        sendEmail: true,
        inquiryData: {
          name: clientName,
          email: clientEmail,
        },
        useClientTemplate: true,
      }),
    });

    const result = await apiResponse.json();
    console.log('📧 Response email result:', result);
    return result.success;
  } catch (error) {
    console.error('Failed to send response email:', error);
    return false;
  }
}

function SubscriptionInquiryModal({
  isOpen,
  onClose,
  inquiry,
  onStatusUpdate,
  onDelete,
  onArchive,
}) {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = actualTheme === 'dark';
  const [responseMessage, setResponseMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [localStatus, setLocalStatus] = useState(inquiry?.status);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [inquiryNotes, setInquiryNotes] = useState([]);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setLocalStatus(inquiry?.status);
      setEmailSent(false);
      setEmailError(null);
      setInquiryNotes(inquiry?.notes || []);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, inquiry]);

  const handleKeyPress = e => {
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleStatusChange = async newStatus => {
    setLocalStatus(newStatus);
    await onStatusUpdate(inquiry.id, newStatus);
  };

  const handleSendEmail = async () => {
    if (!responseMessage.trim()) return;

    setIsSendingEmail(true);
    setEmailError(null);

    try {
      const clientName = inquiry.name || 'Client';
      const packageName = inquiry.planData?.name || 'Subscription Plan';

      const success = await sendResponseEmail(
        inquiry.email,
        clientName,
        responseMessage,
        packageName,
        inquiry.id,
      );

      if (success) {
        setEmailSent(true);
        setEmailError(null);
        await subscriptionService.updateSubscriptionInquiryWithEmailResponse(
          inquiry.id,
          responseMessage,
          currentUser?.uid,
          currentUser?.displayName || 'Admin',
        );
        setShowEmailForm(false);
        setResponseMessage('');
        await onStatusUpdate(inquiry.id, 'responded');
        setLocalStatus('responded');
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        setEmailError('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailError(error.message || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ==================== NOTE SYSTEM ====================
  const handleAddNote = async () => {
    if (!notes.trim()) return;

    try {
      const newNote = {
        id: `note_${Date.now()}`,
        text: notes.trim(),
        addedBy: currentUser?.uid,
        addedByName: currentUser?.displayName || 'Admin',
        addedAt: new Date().toISOString(),
        isEdited: false,
      };

      setInquiryNotes(prev => [...prev, newNote]);
      await subscriptionService.addNote(inquiry.id, newNote, currentUser?.uid);
      setNotes('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  const handleEditNote = async index => {
    const note = inquiryNotes[index];
    if (!note) return;

    try {
      const updatedNote = {
        ...note,
        text: editingNoteText.trim(),
        isEdited: true,
        editedAt: new Date().toISOString(),
      };

      const updatedNotes = [...inquiryNotes];
      updatedNotes[index] = updatedNote;
      setInquiryNotes(updatedNotes);

      await subscriptionService.updateNote(
        inquiry.id,
        note.id,
        updatedNote,
        currentUser?.uid,
      );
      setEditingNoteIndex(null);
      setEditingNoteText('');
    } catch (error) {
      console.error('Error editing note:', error);
      alert('Failed to edit note');
    }
  };

  const handleDeleteNote = async index => {
    const note = inquiryNotes[index];
    if (!note) return;

    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const updatedNotes = inquiryNotes.filter((_, i) => i !== index);
      setInquiryNotes(updatedNotes);
      await subscriptionService.deleteNote(
        inquiry.id,
        note.id,
        currentUser?.uid,
      );
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const startEditingNote = index => {
    setEditingNoteIndex(index);
    setEditingNoteText(inquiryNotes[index]?.text || '');
  };

  const cancelEditingNote = () => {
    setEditingNoteIndex(null);
    setEditingNoteText('');
  };

  const handleArchiveClick = async () => {
    await onArchive(inquiry.id, !inquiry.isArchived);
    onClose();
  };

  const handleDeleteClick = async () => {
    if (window.confirm('Are you sure you want to delete this inquiry?')) {
      await onDelete(inquiry.id);
      onClose();
    }
  };

  if (!isOpen || !inquiry) return null;

  const formatDate = timestamp => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatPrice = (price, billingCycle) => {
    if (!price) return 'Custom';
    return `$${price.toLocaleString()}/${billingCycle === 'monthly' ? 'mo' : 'yr'}`;
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: FiClock, color: '#eab308' },
    { value: 'in-review', label: 'In Review', icon: FiEye, color: '#3b82f6' },
    {
      value: 'responded',
      label: 'Responded',
      icon: FiMessageSquare,
      color: '#8b5cf6',
    },
    {
      value: 'approved',
      label: 'Approved',
      icon: FiCheckCircle,
      color: '#22c55e',
    },
    {
      value: 'declined',
      label: 'Declined',
      icon: FiAlertCircle,
      color: '#ef4444',
    },
    {
      value: 'completed',
      label: 'Completed',
      icon: FiCheckCircle,
      color: '#10b981',
    },
  ];

  const planData = inquiry.planData;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl transition-all animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex justify-between items-center p-5 border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h2 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
            Subscription Inquiry Details
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Client Info */}
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiUser className="w-7 h-7" style={{ color: 'var(--text)' }} />
            </div>
            <div className="flex-1">
              <h3
                className="text-xl font-semibold"
                style={{ color: 'var(--text)' }}
              >
                {inquiry.name}
              </h3>
              <div className="flex flex-wrap gap-3 mt-1">
                <a
                  href={`mailto:${inquiry.email}`}
                  className="text-sm hover:underline flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiMail className="w-3 h-3" /> {inquiry.email}
                </a>
                {inquiry.company && (
                  <span
                    className="text-sm flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiTrendingUp className="w-3 h-3" /> {inquiry.company}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label
              className="text-sm font-medium mb-1 block"
              style={{ color: 'var(--muted)' }}
            >
              Status
            </label>
            <select
              value={localStatus}
              onChange={e => handleStatusChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all cursor-pointer"
              style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Subscription Plan Info */}
          <div
            className="p-4 rounded-xl"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <h4
              className="font-semibold mb-3 flex items-center gap-2"
              style={{ color: 'var(--text)' }}
            >
              <FiCalendar className="w-4 h-4" style={{ color: '#8b5cf6' }} />
              Subscription Plan Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p
                  className="text-lg font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {planData?.name || 'Custom Plan'}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {formatPrice(planData?.price, planData?.billingCycle)}
                  </span>
                  <span
                    className="text-sm px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor:
                        planData?.billingCycle === 'yearly'
                          ? '#10b98115'
                          : '#8b5cf615',
                      color:
                        planData?.billingCycle === 'yearly'
                          ? '#10b981'
                          : '#8b5cf6',
                    }}
                  >
                    {planData?.billingCycle === 'monthly'
                      ? 'Monthly'
                      : 'Yearly'}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  {planData?.description || 'Subscription plan'}
                </p>
              </div>
            </div>
            {planData?.features && planData.features.length > 0 && (
              <div
                className="mt-3 pt-3 border-t"
                style={{ borderColor: 'var(--border-light)' }}
              >
                <p
                  className="text-xs font-medium mb-2"
                  style={{ color: 'var(--muted)' }}
                >
                  Plan Features:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {planData.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <FiCheckCircle className="w-3 h-3 text-green-500" />
                      <span style={{ color: 'var(--text)' }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Message */}
          <div>
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--muted)' }}
            >
              Additional Message
            </p>
            <div
              className="p-4 rounded-xl whitespace-pre-wrap"
              style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
            >
              {inquiry.message || 'No message provided'}
            </div>
          </div>

          {/* Timeline */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <p
              className="text-sm font-medium mb-2 flex items-center gap-1"
              style={{ color: 'var(--muted)' }}
            >
              <FiClock className="w-3 h-3" /> Timeline
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text)' }}>Submitted</span>
                <span style={{ color: 'var(--muted)' }}>
                  {formatDate(inquiry.createdAt)}
                </span>
              </div>
              {inquiry.readAt && (
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--text)' }}>Read</span>
                  <span style={{ color: 'var(--muted)' }}>
                    {formatDate(inquiry.readAt)}
                  </span>
                </div>
              )}
              {inquiry.reviewStartedAt && (
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--text)' }}>Review Started</span>
                  <span style={{ color: 'var(--muted)' }}>
                    {formatDate(inquiry.reviewStartedAt)}
                  </span>
                </div>
              )}
              {inquiry.decisionMadeAt && (
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--text)' }}>Decision Made</span>
                  <span style={{ color: 'var(--muted)' }}>
                    {formatDate(inquiry.decisionMadeAt)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Responses */}
          {inquiry.responses && inquiry.responses.length > 0 && (
            <div>
              <p
                className="text-sm font-medium mb-2"
                style={{ color: 'var(--muted)' }}
              >
                Previous Responses
              </p>
              <div className="space-y-3">
                {inquiry.responses.map((response, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span
                        className="text-xs font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        {response.sentByName}
                        {response.isEmail && (
                          <span
                            className="ml-2 text-xs"
                            style={{ color: '#8b5cf6' }}
                          >
                            📧 Email
                          </span>
                        )}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: 'var(--muted)' }}
                      >
                        {formatDate(response.sentAt)}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      {response.message}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Sent Success */}
          {emailSent && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg animate-fade-in"
              style={{
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            >
              <FiCheck className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-500">
                Email sent successfully to {inquiry.email}
              </span>
            </div>
          )}

          {/* Email Error */}
          {emailError && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg animate-fade-in"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <FiAlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500">{emailError}</span>
            </div>
          )}

          {/* Email Form - Only email response, no client response */}
          {showEmailForm ? (
            <div className="space-y-3">
              <textarea
                rows="4"
                value={responseMessage}
                onChange={e => setResponseMessage(e.target.value)}
                placeholder="Type your email response here..."
                className="w-full px-4 py-3 rounded-xl outline-none resize-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleSendEmail}
                  disabled={isSendingEmail || !responseMessage.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                >
                  {isSendingEmail ? (
                    <>
                      <FiLoader className="w-3 h-3 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiMail className="w-3 h-3" />
                      Send Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowEmailForm(false);
                    setResponseMessage('');
                    setEmailError(null);
                  }}
                  className="px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                  style={{ border: '1px solid var(--border)' }}
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                <FiAtSign className="inline w-3 h-3 mr-1" />
                Email will be sent to: {inquiry.email}
              </p>
            </div>
          ) : (
            <button
              onClick={() => {
                setShowEmailForm(true);
                setResponseMessage(
                  `Dear ${inquiry.name},\n\nThank you for your interest in our ${planData?.name || 'subscription'} plan. We have reviewed your request and would like to discuss the details further.\n\nPlease let us know a convenient time for a call.\n\nBest regards,\nWD Hejbul Team`,
                );
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiMail className="w-4 h-4" />
              Send Email Response
            </button>
          )}

          {/* ==================== NOTES SECTION ==================== */}
          <div>
            <p
              className="text-sm font-medium mb-3 flex items-center gap-2"
              style={{ color: 'var(--muted)' }}
            >
              <FiMessageSquare className="w-4 h-4" />
              Internal Notes
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                {inquiryNotes.length}
              </span>
            </p>

            {/* Add Note */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add internal note..."
                className="flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
                onKeyPress={e => e.key === 'Enter' && handleAddNote()}
              />
              <button
                onClick={handleAddNote}
                disabled={!notes.trim()}
                className="px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80 disabled:opacity-50 flex items-center gap-1"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiPlus className="w-4 h-4" />
                Add
              </button>
            </div>

            {/* Notes List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {inquiryNotes.length === 0 ? (
                <p
                  className="text-sm text-center py-4"
                  style={{ color: 'var(--muted)' }}
                >
                  No notes yet
                </p>
              ) : (
                inquiryNotes.map((note, index) => (
                  <div
                    key={note.id || index}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    {editingNoteIndex === index ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editingNoteText}
                          onChange={e => setEditingNoteText(e.target.value)}
                          className="flex-1 px-2 py-1 rounded text-sm outline-none"
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                          autoFocus
                          onKeyPress={e => {
                            if (e.key === 'Enter') handleEditNote(index);
                            if (e.key === 'Escape') cancelEditingNote();
                          }}
                        />
                        <button
                          onClick={() => handleEditNote(index)}
                          className="px-2 py-1 rounded text-xs transition-all hover:opacity-80"
                          style={{
                            backgroundColor: 'var(--text)',
                            color: 'var(--bg)',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditingNote}
                          className="px-2 py-1 rounded text-xs transition-all hover:opacity-80"
                          style={{ border: '1px solid var(--border)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start">
                          <p
                            className="text-sm"
                            style={{ color: 'var(--text)' }}
                          >
                            {note.text}
                          </p>
                          <div className="flex gap-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => startEditingNote(index)}
                              className="p-1 rounded transition-all hover:bg-black/5 dark:hover:bg-white/5"
                              style={{ color: 'var(--muted)' }}
                            >
                              <FiEdit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(index)}
                              className="p-1 rounded transition-all hover:bg-red-500/10"
                              style={{ color: 'var(--muted)' }}
                            >
                              <FiTrash className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            {note.addedByName || 'Admin'}
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            •
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            {formatDate(note.addedAt)}
                          </span>
                          {note.isEdited && (
                            <>
                              <span
                                className="text-xs"
                                style={{ color: 'var(--muted)' }}
                              >
                                •
                              </span>
                              <span
                                className="text-xs italic"
                                style={{ color: 'var(--muted)' }}
                              >
                                edited
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className="flex flex-wrap gap-2 pt-2 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            {!inquiry.isArchived ? (
              <button
                onClick={handleArchiveClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiArchive className="w-4 h-4" />
                Archive
              </button>
            ) : (
              <button
                onClick={handleArchiveClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiArchive className="w-4 h-4" />
                Unarchive
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              <FiTrash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionInquiryModal;
